import { useAuth } from '@/components/auth/authProvider';
import { isInternalUser } from '@/components/auth/RequireIsInternalUser';
import usePermissions from '@/hooks/usePermissions';
import { Box, Spinner, toast } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { OrganizationType, OrganizationStatus, OrganizationStatusLabels, UserPermissions } from 'types';

import { flattenToOptions } from '../../../helpers/flattenOrgs';
import {
  useEditOrganizationMutation,
  useGetOrganizationDetailsQuery,
  useGetOrganizationTreeQuery,
} from '../../../services/organizations';
import { MainContainer } from '../../MainContainer';
import PageHeader from '../../PageHeader';

import { OrganizationForm, OrganizationFormSchema } from './OrganizationForm';

export const filterOutDisabledFields = (account: any, disabledFields: string[]): any => {
  const filtered = Object.keys(account);
  const filteredObject = filtered
    .filter((key) => !disabledFields.includes(key))
    .reduce((obj, key) => {
      return {
        ...obj,
        [key]: account[key as keyof OrganizationFormSchema],
      };
    }, {});
  return filteredObject;
};

let disabledFields = ['alias', 'parent'];

const organizationStatusOptions = Object.keys(OrganizationStatus).map((key) => ({
  label: OrganizationStatusLabels[key as OrganizationStatus],
  value: key,
}));

export const EditOrganization = () => {
  const { alias } = useParams<{ alias: any }>();
  const { login, organizationData, homeOrganizationData, claims } = useAuth();
  const navigate = useNavigate();
  const { data: org, isLoading: isLoadingOrg } = useGetOrganizationDetailsQuery({ alias });
  const { data: { tree = [] } = {}, isLoading: isLoadingOrgs } = useGetOrganizationTreeQuery();
  const [editOrganization] = useEditOrganizationMutation();

  const isLoading = isLoadingOrg || isLoadingOrgs;
  // TODO: fix this
  const parentOptions = useMemo(() => {
    return flattenToOptions(tree || []);
  }, [tree]);

  const initialValues = useMemo(
    () => ({
      name: org?.name,
      alias: org?.alias,
      parent: parentOptions.find((p) => p.value === org?.parentAlias) || {
        label: `${org?.parentAlias}`,
        value: org?.parentAlias,
      },
      status: organizationStatusOptions.find((s) => s.value === org?.status) || {
        label: OrganizationStatusLabels.unknown,
        value: 'unknown',
      },
      email: org?.email,
      phoneNumber: org?.phoneNumber,
      federalEmployerId: org?.federalEmployerId,
      insuranceCompanyName: org?.insuranceCompanyName,
      insuranceCompanyPhoneNumber: org?.insuranceCompanyPhoneNumber,
      address1: org?.address,
      city: org?.city,
      state: org?.state,
      zip: org?.zip,
      authorizedSigners: org?.authorizedSigners,
      programTypes: org?.programTypes,
      organizationTypes: org?.organizationTypes,
      extendedWarrantyDealerInformation: org?.extendedWarrantyDealerInformation || [],
      statusChangeDescription: '',
      accountBundlingEnabled: org?.accountBundling?.enabled ?? false,
    }),
    [org, parentOptions],
  );

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    const confirmed = window.confirm(
      'By clicking submit, I acknowledge that the information I am submitting is true and accurate and that any misrepresentation shall constitute a breach of the partnership agreement.',
    );

    if (!confirmed) return;

    try {
      setSubmitting(true);

      const fieldsUpdatedByUser = Object.keys(values).reduce((acc, key) => {
        const initial = initialValues[key as keyof typeof initialValues];
        const current = values[key];
        const wasFieldUpdatedByUser = JSON.stringify(initial) !== JSON.stringify(current);

        if (wasFieldUpdatedByUser) {
          (acc as any)[key] = current;
        }
        return acc;
      }, {});

      const {
        address1: address,
        status: organizationStatus,
        statusChangeDescription,
        ...rest
      } = fieldsUpdatedByUser as any;

      const payload: { [key: string]: any } = { ...rest };
      if (address !== undefined) {
        payload.address = address;
      }
      if (organizationStatus !== undefined) {
        payload.status = organizationStatus.value;
        if (statusChangeDescription) {
          payload.statusChangeDescription = statusChangeDescription;
        }
      }

      const result = await editOrganization({
        organization: filterOutDisabledFields(
          {
            ...payload,
            id: org?.id,
          },
          disabledFields,
        ),
        alias,
      });
      if (!('error' in result)) {
        toast.success('Organization modified successfully');
        if (organizationData?.id !== org?.id) {
          navigate(`/settings/organizations/${alias}`);
        } else {
          await login(JSON.stringify({ returnTo: `/settings/organizations/${alias}` }), alias);
        }
      } else {
        if ((result as any).error?.data?.errors) {
          toast.error((result as any).error?.data?.errors.join(', '));
        } else {
          toast.error('Error updating organization');
        }
      }
    } catch (_e) {
      toast.error('Error updating organization');
    }
    setSubmitting(false);
  };

  const isInternal = isInternalUser(claims, true);
  const userPermissions = usePermissions();
  const isOrgAdmin = userPermissions.includes(UserPermissions.admin);
  const disableSubmit = !isInternal && !isOrgAdmin;

  if (!isInternal) {
    disabledFields = [
      'name',
      'alias',
      'parent',
      'label',
      'value',
      'status',
      'email',
      'phoneNumber',
      'federalEmployerId',
      'insuranceCompanyName',
      'insuranceCompanyPhoneNumber',
      'address1',
      'city',
      'state',
      'zip',
      'authorizedSigners',
      'organizationTypes',
      'organizationStatus',
    ];
    if (!isOrgAdmin && !isInternal) {
      disabledFields.push('programTypes');
      disabledFields.push('extendedWarrantyDealerInformation');
    }

    // Home org, not the viewed org, so an admin impersonating a child org can still edit that child.
    if (isOrgAdmin && alias === homeOrganizationData?.alias) {
      disabledFields.push('programTypes');
    }

    if (isOrgAdmin && homeOrganizationData?.organizationTypes?.includes(OrganizationType.integratedPlatform)) {
      disabledFields.push('programTypes');
    }
  }

  return (
    <>
      <Helmet>
        <title>Edit Organization</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          title="Edit Organization"
          eyebrow={<Link to={`/settings/organizations/${alias}`}>{org?.name || '...'}</Link>}
        />
        <Box padding={{ base: '0 lg', tablet: '0' }}>
          {!isLoading ? (
            <OrganizationForm
              initialValues={initialValues}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              parentOptions={parentOptions}
              statusOptions={organizationStatusOptions}
              disabledFields={disabledFields}
              cancelButtonUrl={`/settings/organizations/${alias}`}
              disableSubmit={disableSubmit}
              accountBundlingInheritedFrom={
                org?.accountBundling?.isInherited ? org.accountBundling.source : undefined
              }
            />
          ) : (
            <Spinner size="lg" />
          )}
        </Box>
      </MainContainer>
    </>
  );
};
